import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { IntakeGrnDialog, turnoverBadge } from "@/components/IntakeGrnDialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  IndianRupee, ReceiptText, Percent, Store, Users, TrendingUp, RefreshCw,
  Activity, Gift, Wallet, BarChart3,
} from "lucide-react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function todayYmd() { return new Date().toISOString().slice(0, 10); }
function daysAgoYmd(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

export default function ShowroomMonitorPage() {
  const [from, setFrom] = useState(daysAgoYmd(7));
  const [to, setTo] = useState(todayYmd());
  const [showroomId, setShowroomId] = useState("__all");
  const [showrooms, setShowrooms] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [byShowroom, setByShowroom] = useState([]);
  const [byCashier, setByCashier] = useState([]);
  const [byProduct, setByProduct] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [cash, setCash] = useState([]);
  const [feed, setFeed] = useState([]);
  const [intake, setIntake] = useState({});
  const [drill, setDrill] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/showrooms").then((r) => setShowrooms(r.data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    const params = { from, to };
    if (showroomId && showroomId !== "__all") params.showroom_id = showroomId;
    try {
      const [k, s, c, p, d, cc, lf, ik] = await Promise.all([
        api.get("/showroom-monitor/kpis", { params }),
        api.get("/showroom-monitor/by-showroom", { params: { from, to } }),
        api.get("/showroom-monitor/by-cashier", { params }),
        api.get("/showroom-monitor/by-product", { params }),
        api.get("/showroom-monitor/discounts", { params }),
        api.get("/showroom-monitor/cash-closing", { params }),
        api.get("/showroom-monitor/live-feed", { params: { limit: 15 } }),
        api.get("/ho/showroom-intake", { params: { range: "custom", date_from: from, date_to: to } }).catch(() => ({ data: { rows: [] } })),
      ]);
      setKpis(k.data); setByShowroom(s.data); setByCashier(c.data);
      setByProduct(p.data); setDiscounts(d.data); setCash(cc.data); setFeed(lf.data);
      setIntake(Object.fromEntries((ik.data?.rows || []).map((r) => [r.branch_id, r])));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load monitor");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="showroom-monitor-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="uc-label">Retail · Central Monitor</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            Showroom Monitor
          </h1>
          <p className="text-sm text-[#8B7355] mt-1">
            Live pulse across every retail showroom — bills, revenue, discounts, GST, cash closing.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="mon-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-wrap items-end gap-2 p-3 bg-white border border-[#EAE5D9] rounded-md">
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="mon-from" />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="mon-to" />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Showroom</Label>
          <Select value={showroomId} onValueChange={setShowroomId}>
            <SelectTrigger className="w-56" data-testid="mon-showroom-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">All showrooms</SelectItem>
              {showrooms.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <div className="flex gap-1.5">
          {[
            { label: "Today", from: todayYmd(), to: todayYmd() },
            { label: "7d", from: daysAgoYmd(7), to: todayYmd() },
            { label: "30d", from: daysAgoYmd(30), to: todayYmd() },
          ].map((r) => (
            <Button
              key={r.label}
              type="button" size="sm" variant="outline"
              onClick={() => { setFrom(r.from); setTo(r.to); setTimeout(load, 0); }}
              data-testid={`mon-range-${r.label.toLowerCase()}`}
            >
              {r.label}
            </Button>
          ))}
          <Button type="submit" size="sm" className="bg-[#C05621] text-white">Apply</Button>
        </div>
      </form>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Kpi icon={IndianRupee} tone="emerald" label="Revenue" value={inr(kpis?.revenue)} testid="kpi-revenue" />
        <Kpi icon={ReceiptText} tone="amber" label="Bills" value={kpis?.bills ?? 0} testid="kpi-bills" />
        <Kpi icon={TrendingUp} tone="blue" label="Avg Bill" value={inr(kpis?.avg_bill)} testid="kpi-avg" />
        <Kpi icon={BarChart3} tone="neutral" label="Taxable" value={inr(kpis?.taxable)} testid="kpi-taxable" />
        <Kpi icon={Percent} tone="neutral" label="Total GST" value={inr(kpis?.total_gst)} testid="kpi-gst" />
        <Kpi icon={Gift} tone="rose" label="Discount+Loyalty" value={inr((kpis?.total_discount || 0) + (kpis?.total_loyalty_redeemed || 0))} testid="kpi-discount" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By showroom */}
        <Panel icon={Store} title="Revenue vs Stock Intake by Showroom" testid="panel-by-showroom">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-[#8B7355] border-b">
              <tr><th className="text-left p-2">Showroom</th><th className="text-right p-2">Bills</th><th className="text-right p-2">Revenue</th><th className="text-right p-2">GST</th><th className="text-right p-2 border-l border-[#EAE5D9]">Stock intake</th><th className="text-left p-2">Sales-to-intake</th></tr>
            </thead>
            <tbody className="divide-y">
              {(() => {
                const ids = new Set(byShowroom.map((r) => r.branch_id));
                const extra = Object.values(intake).filter((i) => !ids.has(i.branch_id) && i.qty_received > 0).map((i) => ({ branch_id: i.branch_id, branch_name: i.branch_name, branch_code: i.branch_code, bills: 0, revenue: 0, gst: 0 }));
                return [...byShowroom, ...extra];
              })().map((r) => { const ik = intake[r.branch_id]; const tb = turnoverBadge(ik); return (
                <tr key={r.branch_id} className={ik?.turnover === "low" ? "bg-red-50/40" : ""} data-testid={`sm-showroom-row-${r.branch_id}`}>
                  <td className="p-2 font-medium text-[#5C4033]">{r.branch_name} <span className="text-[10px] text-[#8B7355]">· {r.branch_code}</span></td>
                  <td className="p-2 text-right">{r.bills}</td>
                  <td className="p-2 text-right font-semibold text-emerald-700">
                    {inr(r.revenue)}
                    {r.is_franchisee && (
                      <div className="mt-0.5 text-[10px] font-normal leading-tight whitespace-nowrap" data-testid={`sm-split-${r.branch_id}`}>
                        <span className="text-[#C05621]">Owner {inr(r.owner_share || 0)}</span>
                        <span className="text-[#8B7355]"> · </span>
                        <span className="text-emerald-700">Franchisee {inr(r.franchisee_share || 0)}</span>
                      </div>
                    )}
                  </td>
                  <td className="p-2 text-right">{inr(r.gst)}</td>
                  <td className="p-2 text-right border-l border-[#EAE5D9]">
                    {ik ? (
                      <button onClick={() => setDrill(ik)} className="text-right hover:text-[#C05621]" data-testid={`sm-intake-value-${r.branch_id}`} title="View GRNs received in this period">
                        <div className="font-semibold underline decoration-dotted underline-offset-4">{inr(ik.value_received)}</div>
                        <div className="text-[10px] text-[#8B7355]">{ik.qty_received} units · {ik.grn_count} GRN</div>
                      </button>
                    ) : <span className="text-[#8B7355]">—</span>}
                  </td>
                  <td className="p-2"><Badge variant="outline" className={`${tb.cls} text-[10px]`} data-testid={`sm-turnover-${r.branch_id}`}>{tb.label}</Badge></td>
                </tr>
              ); })}
              {byShowroom.length === 0 && Object.keys(intake).length === 0 && <tr><td colSpan={6} className="p-4 text-center text-[#8B7355]">No sales or intake in range</td></tr>}
            </tbody>
          </table>
          <IntakeGrnDialog branch={drill} dateFrom={from} dateTo={to} onClose={() => setDrill(null)} />
        </Panel>

        {/* By cashier */}
        <Panel icon={Users} title="Cashier Leaderboard" testid="panel-by-cashier">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-[#8B7355] border-b">
              <tr><th className="text-left p-2">Cashier</th><th className="text-right p-2">Bills</th><th className="text-right p-2">Revenue</th><th className="text-right p-2">Avg</th></tr>
            </thead>
            <tbody className="divide-y">
              {byCashier.slice(0, 10).map((r) => (
                <tr key={r.user_id}>
                  <td className="p-2 font-medium">{r.name || r.email || "—"} <span className="text-[10px] text-[#8B7355]">· {r.role}</span></td>
                  <td className="p-2 text-right">{r.bills}</td>
                  <td className="p-2 text-right font-semibold text-emerald-700">{inr(r.revenue)}</td>
                  <td className="p-2 text-right">{inr(r.avg_bill)}</td>
                </tr>
              ))}
              {byCashier.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-[#8B7355]">No cashier activity</td></tr>}
            </tbody>
          </table>
        </Panel>

        {/* Cash closing */}
        <Panel icon={Wallet} title="Cash Closing · Payment Mix" testid="panel-cash-closing">
          <div className="space-y-3">
            {cash.map((b) => (
              <div key={b.branch_id} className="border border-[#EAE5D9] rounded p-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-sm text-[#5C4033]">{b.branch_name}</div>
                  <div className="text-sm font-semibold text-emerald-700">{inr(b.total)}</div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(b.methods).map(([m, v]) => (
                    <Badge key={m} variant="outline" className="text-[10px]">
                      {m.toUpperCase()}: {inr(v.amount)} · {v.count}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
            {cash.length === 0 && <div className="p-4 text-center text-[#8B7355] text-xs">No payments in range</div>}
          </div>
        </Panel>

        {/* Top products */}
        <Panel icon={BarChart3} title="Top Products" testid="panel-by-product">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-[#8B7355] border-b">
              <tr><th className="text-left p-2">Product</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Revenue</th></tr>
            </thead>
            <tbody className="divide-y">
              {byProduct.slice(0, 15).map((r) => (
                <tr key={r.product_id}>
                  <td className="p-2 text-[#5C4033]">{r.name || r.product_id}</td>
                  <td className="p-2 text-right">{r.qty}</td>
                  <td className="p-2 text-right font-semibold text-emerald-700">{inr(r.revenue)}</td>
                </tr>
              ))}
              {byProduct.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-[#8B7355]">No products sold in range</td></tr>}
            </tbody>
          </table>
        </Panel>
      </div>

      {/* Discount audit */}
      <Panel icon={Gift} title={`Discount Audit (${discounts.length})`} testid="panel-discounts">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-[#8B7355] border-b">
              <tr>
                <th className="text-left p-2">Bill #</th>
                <th className="text-left p-2">When</th>
                <th className="text-left p-2">Showroom</th>
                <th className="text-left p-2">Authorised by</th>
                <th className="text-left p-2">Reason</th>
                <th className="text-right p-2">Line Disc</th>
                <th className="text-right p-2">Footer</th>
                <th className="text-right p-2">Loyalty</th>
                <th className="text-right p-2">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {discounts.map((d) => (
                <tr key={d.sale_id}>
                  <td className="p-2 font-mono">{d.number}</td>
                  <td className="p-2 text-[#8B7355]">{d.date?.slice(0, 16).replace("T", " ")}</td>
                  <td className="p-2">{d.branch_name}</td>
                  <td className="p-2">{d.authorised_by_name || <span className="text-[#8B7355] italic">self</span>}</td>
                  <td className="p-2 text-[#8B7355]">{d.reason || "—"}</td>
                  <td className="p-2 text-right">{inr(d.line_discount)}</td>
                  <td className="p-2 text-right">{inr(d.footer_discount)}</td>
                  <td className="p-2 text-right">{inr(d.loyalty_redeemed)}</td>
                  <td className="p-2 text-right font-semibold">{inr(d.grand_total)}</td>
                </tr>
              ))}
              {discounts.length === 0 && <tr><td colSpan={9} className="p-4 text-center text-[#8B7355]">No discounts applied in range</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* Live feed */}
      <Panel icon={Activity} title="Live Sales Feed" testid="panel-live-feed">
        <div className="space-y-1.5">
          {feed.map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-xs border-b border-dashed border-[#EAE5D9] pb-1.5 last:border-0">
              <span className="font-mono text-[#8B7355]">{r.number}</span>
              <span className="text-[#5C4033]">{r.branch_name}</span>
              <span className="text-[#8B7355]">·</span>
              <span className="text-[#5C4033] flex-1 truncate">{(r.customer_snapshot || {}).name || "Walk-in"}</span>
              {r.discount_amount > 0 && <Badge className="border-0 bg-amber-500 text-white text-[10px]">-{inr(r.discount_amount)}</Badge>}
              <span className="font-semibold text-emerald-700">{inr(r.grand_total)}</span>
              <span className="text-[10px] text-[#8B7355]">{r.created_at?.slice(11, 16)}</span>
            </div>
          ))}
          {feed.length === 0 && <div className="p-4 text-center text-[#8B7355] text-xs">No recent sales</div>}
        </div>
      </Panel>
    </div>
  );
}

function Kpi({ icon: Icon, tone, label, value, testid }) {
  const bg = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    neutral: "bg-white border-[#EAE5D9] text-[#5C4033]",
  }[tone] || "bg-white border-[#EAE5D9]";
  return (
    <div className={`border rounded-lg p-3 ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-xl font-[Outfit] font-semibold mt-0.5 truncate">{value}</div>
    </div>
  );
}

function Panel({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden" data-testid={testid}>
      <div className="p-3 border-b border-[#EAE5D9] flex items-center gap-2 bg-[#FEF9EA]">
        <Icon className="w-4 h-4 text-[#C05621]" />
        <div className="font-[Outfit] font-semibold text-sm">{title}</div>
      </div>
      <div className="p-2 max-h-[420px] overflow-y-auto">{children}</div>
    </div>
  );
}
